import { TbFileText } from 'react-icons/tb'

import { CheckDictionaryPageAccessTask, TopMenu, serviceFields } from '@/Containers/SettingSection/ListContainer/Hooks'
import { ReloadDataActionHandlerTask } from '@/Core/UI/CrudResource/Tasks/ReloadDataActionHandlerTask'
import { PRINTABLE_PROVIDER_OPTIONS } from '@/Ship/Contracts/PrintableProvider'

import { documentTypeFormTemplate } from '../Components/DocumentTypeTemplateSyntax'

import type { IResourceConfig } from '@/Core/Contracts/UI'

/** Мінімум форми, з яким працюють `depFn` і обробники файлового поля. */
interface TemplateFormContext {
    contextKey?: string
    values?: { uuid?: unknown }
    getInitialValue?: (path: string) => unknown
    setInitialValue?: (path: string, value: unknown) => void
    setValue?: (path: string, value: unknown) => void
}

interface TemplateFieldEvent {
    field: { path: string }
    formContext?: TemplateFormContext
    crudContext?: unknown
}

const savedUuid = (formContext: TemplateFormContext | undefined): string | null => {
    const uuid = formContext?.values?.uuid

    return typeof uuid === 'string' && uuid !== '' ? uuid : null
}

/**
 * Картку перечитуємо з сервера після завантаження й видалення шаблону
 * (`SPEC-012` §9.1, як у легасі): шаблон замінює попередній на беку, а
 * FileInput ядра лише дописує відповідь до наявного значення.
 */
const reloadCard = async (crudContext: unknown): Promise<void> => {
    try {
        await ReloadDataActionHandlerTask({ crudContext })
    } catch {
        // Не перечиталося — у полі однаково лишився актуальний файл (нижче)
    }
}

/**
 * Довідник «Типи документів» (`SPEC-012` §9.1, фаза 1).
 *
 * Тип документа — довідник друкованих форм: провайдер каже, чий це документ
 * (клієнта чи перевізника), а шаблон .docx — з чого його генерувати (фаза 2).
 *
 * Шаблон вантажиться одним кроком у вже створений тип (P-05, `SPEC-002` G-05):
 * `POST /services/document_types/{uuid}/template`, новий замінює старий (P-07),
 * видаляє його FileInput за `@id` файлу. Посилання на файл — підписане, з
 * терміном 1 год (ADR 042).
 *
 * Ресурс живе в `PrintableContainer` (рішення R-01), а на сторінці «Довідники»
 * стоїть у групі «Реквізити й документи» (`LIST_GROUPS`). Сторінку бачить лише
 * адміністратор (`pageConfig.accessFn`), читати типи для меню генерації може
 * кожен.
 */
export const ListDocumentTypeResource: IResourceConfig = {
    title: 'Типи документів',
    fields: {
        title: {
            label: 'Назва',
            type: 'string',
            isRequired: true,
        },
        provider: {
            label: 'Провайдер',
            type: 'select',
            options: PRINTABLE_PROVIDER_OPTIONS,
            isRequired: true,
        },
        template: {
            label: 'Шаблон',
            type: 'file',
            isMultiple: false,
            uploadUrl: '/services/document_types/{uuid}/template',
            uploadUrlReplacer: ({ uploadUrl, formContext }: { uploadUrl: string; formContext?: TemplateFormContext }) =>
                uploadUrl.replace('{uuid}', savedUuid(formContext) ?? ''),
            uploadAccept: ['docx'],
            // Межа ADR 042: ядровий nginx більше 10 МБ не пропускає
            uploadMaxSizeMb: 10,
            showViewIcon: false,
            showEditIcon: false,
            // Шаблон — лише в збереженому типі (P-05): у нового ще немає uuid
            depFn: ({ formContext }: { formContext?: TemplateFormContext }) => {
                if (formContext?.contextKey === 'filterFormContext') return undefined

                return savedUuid(formContext) === null ? false : undefined
            },
            eventHanlers: {
                onAfterUpload: {
                    keepLatestTemplate: ({ field, formContext, crudContext }: TemplateFieldEvent): void => {
                        // FileInput дописує відповідь до старого значення, а
                        // шаблон у типу один: лишаємо щойно завантажений
                        const value = formContext?.getInitialValue?.(field.path)
                        const latest = Array.isArray(value) ? (value[value.length - 1] ?? null) : (value ?? null)

                        formContext?.setInitialValue?.(field.path, latest)
                        formContext?.setValue?.(field.path, latest)

                        void reloadCard(crudContext)
                    },
                },
            },
            // ⚠️ Обробники видалення ядровий FileInput шукає в `props`, а
            // завантаження — у самому описі поля (`FileInput.tsx`, onRemove)
            props: {
                eventHanlers: {
                    onAfterDelete: {
                        reloadCard: ({ crudContext }: TemplateFieldEvent): void => {
                            void reloadCard(crudContext)
                        },
                    },
                },
            },
        },
        isUseInClient: {
            label: 'Використовувати в картці клієнта',
            type: 'switch',
        },
        isUseInCarrier: {
            label: 'Використовувати в картці перевізника',
            type: 'switch',
        },
        isUseInApplication: {
            label: 'Використовувати в заявках',
            type: 'switch',
        },
        sorting: {
            label: 'Сортування',
            type: 'number',
            defaultValue: 500,
        },
        ...serviceFields(),
    },
    table: {
        showColumns: [
            'id',
            'title',
            'provider',
            'template',
            'isUseInClient',
            'isUseInCarrier',
            'isUseInApplication',
            'sorting',
        ],
    },
    filter: {
        // «Провайдер» у легасі бек мовчки ігнорував (N-09) — тут фільтрує
        includeFields: ['id', 'title', 'provider'],
    },
    // Довідка про змінні шаблону — над полем «Шаблон» (`SPEC-012` §9.1, §13)
    form: {
        template: documentTypeFormTemplate,
    },
    places: {
        top: <TopMenu current="document-types" />,
    },
    restUrl: '/services/document_types',
    pageUrl: '/settings/document-types',
    pageConfig: {
        title: 'Типи документів',
        icon: <TbFileText />,
        accessFn: CheckDictionaryPageAccessTask,
    },
    settings: {
        searchFields: ['id', 'title'],
    },
}
